<!--
  REV-21365 — Kommentare am Konto (Pure View).

  Zustands-Vorrang wie beim Aktivitätslog: lädt > Fehler > leer > gefüllt. Der Kopf bleibt in
  jedem Zustand stehen, damit die Fläche nicht als Ganzes verschwindet.

  Tests hängen an `data-testid` und Daten-Attributen, NIE an Klassen oder Farben.
-->
<div class="notes" data-testid="account-notes">
    <!--
        Kopf nach dem Prototyp: Symbol · Wort · Zähler-Plakette. Vorher stand hier ein Satz
        („3 Kommentare · Konto 1022"), der die Zahl im Fließtext versteckte — die Plakette ist das,
        was der Leser sucht. Das Konto bleibt daneben stehen, leiser: diese Fläche ist EINE von
        mehreren Spalten am Konto, und ohne Nennung ist nicht klar, wessen Kommentare das sind.
    -->
    <div class="notes-header" data-testid="account-notes-header">
        <lucide-icon class="notes-header__icon" name="message-square" [size]="15" aria-hidden="true"></lucide-icon>
        <span class="notes-header__label">{{ 'aumico_abcp_notes_count' | i18next: { count: totalCount } }}</span>
        <span class="notes-header__badge">{{ totalCount }}</span>
        <span class="notes-header__account" *ngIf="accountLabel">{{ accountLabel }}</span>
    </div>

    <div class="notes-list" data-testid="account-notes-list" #list>
        <div class="notes-loading" data-testid="account-notes-loading" *ngIf="loading; else notLoading">
            <div class="notes-skeleton-row" *ngFor="let row of skeletonRows" aria-hidden="true">
                <div class="notes-skeleton notes-skeleton--dot"></div>
                <div class="notes-skeleton-lines">
                    <div class="notes-skeleton notes-skeleton--meta"></div>
                    <div class="notes-skeleton"></div>
                </div>
            </div>
        </div>

        <ng-template #notLoading>
            <div class="notes-error" data-testid="account-notes-error" *ngIf="error; else notError">
                {{ 'aumico_abcp_notes_error' | i18next }}
            </div>

            <ng-template #notError>
                <div class="notes-empty" data-testid="account-notes-empty" *ngIf="isEmpty; else filled">
                    {{ 'aumico_abcp_notes_empty' | i18next }}
                </div>

                <ng-template #filled>
                    <article
                        class="note"
                        data-testid="account-note"
                        *ngFor="let entry of entries; trackBy: trackById"
                        [attr.data-note-id]="entry.id"
                        [attr.data-kind]="entry.kind"
                        [attr.data-source]="entry.source"
                        [attr.data-actor-type]="entry.author.type"
                        [class.note--done]="entry.done"
                        [class.note--active]="isActive(entry)"
                        [attr.data-active]="isActive(entry) ? 'true' : null"
                    >
                        <span class="note__avatar" aria-hidden="true">
                            {{ entry.author.type === 'agent' ? '✦' : entry.author.initials || '?' }}
                        </span>

                        <div class="note__body">
                            <!--
                                Name OBEN, Zeit darunter — zwei Zeilen, wie im Prototyp. In einer
                                Zeile nebeneinander lasen sich Name, Zeit und Herkunft als eine
                                lange graue Kette, in der der Name unterging.
                            -->
                            <div class="note__meta">
                                <span class="note__who">
                                    <span class="note__author" data-testid="note-author">
                                        {{ entry.author.name || ('aumico_abcp_notes_author_unknown' | i18next) }}
                                    </span>
                                    <span class="note__sub">
                                        <span class="note__time" data-testid="note-time">{{ entry.timeLabel }}</span>
                                        <!--
                                            Die Herkunft steht LEISE neben der Zeit, nicht als
                                            großgeschriebene Marke am rechten Rand. Dort zog sie den
                                            Blick auf sich wie ein Status — dabei ist sie eine
                                            Randnotiz, und der Prototyp zeigt sie gar nicht.
                                        -->
                                        <span class="note__source" data-testid="note-source">
                                            · {{ 'aumico_abcp_notes_source_' + entry.source | lowercase | i18next }}
                                        </span>
                                    </span>
                                </span>

                                <!-- Der Menü-Knopf steht IMMER; die Punkte darin hängen am Recht. -->
                                <span class="note__actions" #actions *ngIf="!isEditing(entry)">
                                    <ham-button
                                        class="note__actions-btn"
                                        data-testid="note-actions"
                                        variant="transparent"
                                        size="toolbar"
                                        [hideText]="true"
                                        icon="assets/hamIcons/dots-vertical.svg"
                                        (buttonClick)="toggleMenu(entry, actions)"
                                    ></ham-button>
                                    <!-- In the flyout layer: a click anywhere else closes it, like every other menu. -->
                                    <span
                                        class="note__menu"
                                        data-testid="note-menu"
                                        hamFlyout
                                        *ngIf="openMenuNoteId === entry.id && menuPosition"
                                        [position]="menuPosition"
                                        [style.transform]="menuPosition.isAbove ? 'translateY(-100%)' : null"
                                        (closeFlyout)="closeMenu()"
                                    >
                                        <button
                                            type="button"
                                            data-testid="note-edit"
                                            *ngIf="entry.canEdit"
                                            (click)="startEdit(entry)"
                                        >
                                            {{ 'aumico_abcp_notes_edit' | i18next }}
                                        </button>
                                        <button
                                            type="button"
                                            data-testid="note-delete"
                                            *ngIf="entry.canDelete"
                                            (click)="askDelete(entry)"
                                        >
                                            {{ 'aumico_abcp_notes_delete' | i18next }}
                                        </button>
                                    </span>
                                </span>
                            </div>

                            <!-- Marken: genau `internal` bleibt unbeschriftet (AC2). -->
                            <div class="note__tags" *ngIf="!isEditing(entry) && visibleTags(entry).length">
                                <span
                                    class="note__tag"
                                    data-testid="note-tag"
                                    *ngFor="let tag of visibleTags(entry)"
                                    [attr.data-tag]="tag"
                                >
                                    {{ 'aumico_abcp_notes_tag_' + tag | lowercase | i18next }}
                                </span>
                            </div>

                            <!-- Zitat: klickbar NUR mit Verweis (AC10). -->
                            <div
                                class="note__anchor"
                                data-testid="note-anchor"
                                *ngIf="entry.anchor && !isEditing(entry)"
                                [attr.data-block-id]="entry.anchor.blockId || null"
                                [attr.data-document-id]="entry.anchor.documentId || null"
                                [class.note__anchor--linked]="entry.anchor.blockId || entry.anchor.documentId"
                                [attr.data-page]="entry.anchor.page ?? null"
                                (click)="onAnchorClick(entry)"
                            >
                                <ng-container *ngIf="entry.anchor.snippet; else areaAnchor">„{{ entry.anchor.snippet }}"</ng-container>
                                <!-- REV-21369 — Flächen-Markierung im Beleg: kein Text, also die Seite. -->
                                <ng-template #areaAnchor>{{
                                    'aumico_abcp_notes_anchor_area' | i18next: { page: entry.anchor.page }
                                }}</ng-template>
                            </div>

                            <div class="note__text" data-testid="note-text" *ngIf="!isEditing(entry)">
                                <ng-container *ngFor="let seg of segments(entry.text, entry.mentions)">
                                    <span class="note__mention" data-testid="note-mention" *ngIf="seg.mention">{{ seg.text }}</span>
                                    <ng-container *ngIf="!seg.mention">{{ seg.text }}</ng-container>
                                </ng-container>
                            </div>

                            <!-- Bearbeiten an Ort und Stelle. Kontrolliert: `editingNoteId` kommt herein. -->
                            <div class="note__edit" data-testid="note-edit-form" *ngIf="isEditing(entry)">
                                <div class="note__tags" *ngIf="entry.kind === 'TODO'">
                                    <ham-filter-pill
                                        *ngFor="let tag of tagsFor(entry.kind)"
                                        [attr.data-tag]="tag"
                                        [label]="'aumico_abcp_notes_tag_' + tag | lowercase | i18next"
                                        [active]="isEditTagOn(tag)"
                                        (pillClick)="toggleEditTag(tag)"
                                    ></ham-filter-pill>
                                </div>
                                <abcp-mention-input
                                    [rows]="3"
                                    [value]="editText"
                                    (valueChange)="editText = $event"
                                    [mentions]="editMentions"
                                    (mentionsChange)="editMentions = $event"
                                    [users]="users"
                                    [currentUserId]="currentUserId"
                                ></abcp-mention-input>
                                <div class="note__edit-actions">
                                    <ham-button
                                        data-testid="note-edit-save"
                                        variant="primary"
                                        size="toolbar"
                                        (buttonClick)="saveEdit(entry)"
                                    >
                                        {{ 'aumico_abcp_notes_save' | i18next }}
                                    </ham-button>
                                    <ham-button
                                        data-testid="note-edit-cancel"
                                        variant="white"
                                        size="toolbar"
                                        (buttonClick)="cancelEdit()"
                                    >
                                        {{ 'aumico_abcp_notes_cancel' | i18next }}
                                    </ham-button>
                                </div>
                            </div>

                            <!-- Löschen fragt nach; erst die Bestätigung meldet (AC8). -->
                            <div class="note__confirm" *ngIf="confirmDeleteNoteId === entry.id">
                                <span>{{ 'aumico_abcp_notes_delete_confirm' | i18next }}</span>
                                <ham-button
                                    data-testid="note-delete-confirm"
                                    variant="red"
                                    [outline]="true"
                                    size="toolbar"
                                    (buttonClick)="confirmDelete(entry)"
                                >
                                    {{ 'aumico_abcp_notes_delete' | i18next }}
                                </ham-button>
                                <ham-button
                                    data-testid="note-delete-cancel"
                                    variant="white"
                                    size="toolbar"
                                    (buttonClick)="cancelDelete()"
                                >
                                    {{ 'aumico_abcp_notes_cancel' | i18next }}
                                </ham-button>
                            </div>

                            <!-- Thread: eine Ebene tief, zugeklappt nur die Zusammenfassung (AC9). -->
                            <button
                                type="button"
                                class="note__thread-summary"
                                data-testid="note-thread-summary"
                                *ngIf="entry.replyCount > 0 && !isEditing(entry)"
                                [attr.aria-expanded]="isExpanded(entry)"
                                (click)="threadToggle.emit(entry.id)"
                            >
                                <span>{{ entry.replyCount }} {{ 'aumico_abcp_notes_replies' | i18next: { count: entry.replyCount } }}</span>
                                <span class="note__thread-time" *ngIf="entry.lastReplyTimeLabel">
                                    {{ entry.lastReplyTimeLabel }}
                                </span>
                            </button>

                            <!--
                                ⚠️ „Antworten" muss IMMER da sein. Vorher hing der einzige Weg in
                                den Faden an der Zusammenfassung, und die erscheint nur, wenn es
                                schon Antworten GIBT — auf einen Kommentar ohne Antwort konnte man
                                also nicht antworten. Das ist der Normalfall, nicht der Randfall.
                            -->
                            <button
                                type="button"
                                class="note__reply-open"
                                data-testid="note-reply-open"
                                *ngIf="entry.replyCount === 0 && !isEditing(entry)"
                                [attr.aria-expanded]="isExpanded(entry)"
                                (click)="threadToggle.emit(entry.id)"
                            >
                                <lucide-icon class="note__reply-icon" name="message-square" [size]="12" aria-hidden="true"></lucide-icon>
                                <span>{{ 'aumico_abcp_notes_reply_open' | i18next }}</span>
                            </button>

                            <div class="note__thread" data-testid="note-thread" *ngIf="isExpanded(entry) && entry.replies.length">
                                <div class="note__reply" data-testid="note-reply" *ngFor="let reply of entry.replies">
                                    <span class="note__reply-author">
                                        {{ reply.author.name || ('aumico_abcp_notes_author_unknown' | i18next) }}
                                    </span>
                                    <span class="note__reply-time">{{ reply.timeLabel }}</span>
                                    <span class="note__reply-text">
                                        <ng-container *ngFor="let seg of segments(reply.text, reply.mentions)">
                                            <span class="note__mention" *ngIf="seg.mention">{{ seg.text }}</span>
                                            <ng-container *ngIf="!seg.mention">{{ seg.text }}</ng-container>
                                        </ng-container>
                                    </span>
                                </div>
                            </div>

                            <div class="note__reply-box" *ngIf="isExpanded(entry) && !isEditing(entry)">
                                <abcp-mention-input
                                    variant="reply"
                                    fieldTestId="note-reply-input"
                                    [rows]="1"
                                    [submitOnEnter]="true"
                                    [placeholder]="'aumico_abcp_notes_reply_placeholder' | i18next"
                                    [value]="replyDraft(entry)"
                                    (valueChange)="setReplyDraft(entry, $event)"
                                    [mentions]="replyMentionsOf(entry)"
                                    (mentionsChange)="setReplyMentions(entry, $event)"
                                    [users]="users"
                                    [currentUserId]="currentUserId"
                                    (enterPressed)="sendReply(entry)"
                                ></abcp-mention-input>
                                <button
                                    type="button"
                                    class="note__reply-send"
                                    data-testid="note-reply-send"
                                    [attr.aria-label]="'aumico_abcp_notes_send' | i18next"
                                    [hamTooltip]="'aumico_abcp_notes_send' | i18next"
                                    tooltipClass="abcp"
                                    [disabled]="!replyDraft(entry).trim()"
                                    (click)="sendReply(entry)"
                                >
                                    <lucide-icon name="send" [size]="14" aria-hidden="true"></lucide-icon>
                                </button>
                            </div>
                        </div>
                    </article>
                </ng-template>
            </ng-template>
        </ng-template>
    </div>

    <div class="notes-composer-bar">
        <ham-button
            class="notes-composer-open"
            data-testid="note-composer-open"
            *ngIf="!composerOpen"
            variant="white"
            size="sm"
            icon="assets/hamIcons/plus-outline.svg"
            (buttonClick)="openComposer()"
        >
            {{ 'aumico_abcp_notes_compose' | i18next }}
        </ham-button>

        <div class="notes-composer" data-testid="note-composer" *ngIf="composerOpen">
            <div class="notes-composer__tags">
                <ham-filter-pill
                    data-testid="note-composer-tag"
                    *ngFor="let tag of tagsFor('COMMENT')"
                    [attr.data-tag]="tag"
                    [label]="'aumico_abcp_notes_tag_' + tag | lowercase | i18next"
                    [active]="isComposerTagOn(tag)"
                    (pillClick)="toggleComposerTag(tag)"
                ></ham-filter-pill>
            </div>

            <!-- Zitat-Kästchen nur mit Auswahl, und dann vorausgewählt (AC11). -->
            <!--
                `hamCheckbox` stays empty: the directive moves its content once, so a binding inside it
                would stop updating. A click on the quote toggles it too.
            -->
            <div
                class="notes-composer__anchor"
                data-testid="note-composer-anchor"
                *ngIf="selection"
                (click)="setAttachAnchor(!composerAttachAnchor)"
            >
                <span
                    hamCheckbox
                    data-testid="note-composer-anchor-check"
                    [checked]="composerAttachAnchor"
                    [attr.data-checked]="composerAttachAnchor"
                    (change)="setAttachAnchor($event.checked)"
                ></span>
                <span class="notes-composer__anchor-text">„{{ selection.snippet }}"</span>
            </div>

            <!-- Die eigene Person steht nicht in der `@`-Liste (AC6). -->
            <abcp-mention-input
                class="notes-composer__input"
                fieldTestId="note-composer-text"
                [rows]="2"
                [submitOnEnter]="true"
                [placeholder]="'aumico_abcp_notes_placeholder_comment' | i18next"
                [value]="composerText"
                (valueChange)="composerText = $event"
                [mentions]="composerMentions"
                (mentionsChange)="composerMentions = $event"
                [users]="users"
                [currentUserId]="currentUserId"
                (enterPressed)="send()"
            ></abcp-mention-input>

            <div class="notes-composer__actions">
                <span class="notes-composer__hint">{{ 'aumico_abcp_notes_mention_hint' | i18next }}</span>
                <ham-button
                    data-testid="note-composer-cancel"
                    variant="white"
                    size="toolbar"
                    (buttonClick)="cancelComposer()"
                >
                    {{ 'aumico_abcp_notes_cancel' | i18next }}
                </ham-button>
                <ham-button
                    data-testid="note-composer-send"
                    variant="primary"
                    size="toolbar"
                    [disabled]="!canSend"
                    (buttonClick)="send()"
                >
                    {{ 'aumico_abcp_notes_send' | i18next }}
                </ham-button>
            </div>
        </div>
    </div>
</div>
